import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MyProfile, ProfileService } from '../../domain/services/profile/profile.service';
import { BreadcrumbService } from '../../domain/services/breadcrumb/breadcrumb.service';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, MatCardModule, MatFormFieldModule, MatInputModule,
    MatButtonModule, MatIconModule],
  template: `
    <div class="profile-page">
      <div class="page-header">
        <mat-icon class="page-icon">account_circle</mat-icon>
        <div>
          <div class="page-title">My profile</div>
          <div class="page-description">Your own details. Department and role are set by an administrator.</div>
        </div>
      </div>

      <mat-card class="card" *ngIf="profile as p">
        <div class="row">
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>First name</mat-label>
            <input matInput [(ngModel)]="firstName" maxlength="100" />
          </mat-form-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Last name</mat-label>
            <input matInput [(ngModel)]="lastName" maxlength="100" />
          </mat-form-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Phone</mat-label>
            <input matInput [(ngModel)]="phoneNumber" maxlength="40" />
          </mat-form-field>
        </div>

        <div class="readonly">
          <div class="ro"><span class="k">Username / email</span><span class="v">{{ p.username }}</span></div>
          <div class="ro"><span class="k">Department</span><span class="v">{{ p.paymentOfficeName || '—' }}</span></div>
          <div class="ro"><span class="k">Role</span><span class="v">{{ p.userGroupName || '—' }}</span></div>
          <div class="ro"><span class="k">Last login</span>
            <span class="v">{{ p.lastLoginDate ? (p.lastLoginDate | date:'MM/dd/yyyy h:mm a') : '—' }}</span></div>
        </div>
        <p class="note">Your username is also your sign-in email — an administrator changes it.</p>

        <div class="actions">
          <button mat-stroked-button (click)="reset()" [disabled]="saving || !dirty">Cancel</button>
          <button mat-flat-button color="primary" (click)="save()" [disabled]="saving || !dirty || !valid">
            <mat-icon>save</mat-icon> Save changes
          </button>
        </div>
      </mat-card>

      <div class="empty" *ngIf="!profile && !loading">Could not load your profile.</div>
    </div>
  `,
  styles: [`
    .profile-page { padding:24px 32px; display:flex; flex-direction:column; gap:24px; max-width:900px; }
    .page-header { display:flex; align-items:center; gap:16px; }
    .page-icon { font-size:36px; width:36px; height:36px; color:rgba(0,0,0,.6); }
    .page-title { color:rgba(0,0,0,.6); font-size:20px; font-weight:500; }
    .page-description { color:rgba(0,0,0,.45); font-size:13px; }
    .card { padding:20px; display:flex; flex-direction:column; gap:20px; }
    .row { display:flex; gap:12px; flex-wrap:wrap; }
    .row mat-form-field { width:240px; }
    .readonly { display:grid; grid-template-columns:repeat(auto-fit,minmax(220px,1fr)); gap:12px 24px; }
    .ro { display:flex; flex-direction:column; gap:2px; }
    .ro .k { font-size:12px; text-transform:uppercase; letter-spacing:.05em; color:rgba(0,0,0,.55); }
    .ro .v { font-size:14px; color:#041E49; }
    .note { margin:0; font-size:13px; color:rgba(0,0,0,.5); }
    .actions { display:flex; gap:8px; justify-content:flex-end; }
    .empty { color:rgba(0,0,0,.5); }
  `],
})
export class ProfileComponent implements OnInit {
  private svc = inject(ProfileService);
  private snack = inject(MatSnackBar);
  private breadcrumbs = inject(BreadcrumbService);

  profile: MyProfile | null = null;
  loading = true;
  saving = false;
  firstName = '';
  lastName = '';
  phoneNumber = '';

  ngOnInit(): void {
    this.breadcrumbs.updateBreadcrumbs([{ icon: 'account_circle', url: '/app/profile', label: 'My profile' }]);
    this.svc.me().subscribe({
      next: p => { this.profile = p; this.reset(); this.loading = false; },
      error: () => { this.loading = false; },
    });
  }

  get dirty(): boolean {
    const p = this.profile;
    if (!p) return false;
    return this.firstName !== (p.firstName ?? '')
      || this.lastName !== (p.lastName ?? '')
      || this.phoneNumber !== (p.phoneNumber ?? '');
  }

  get valid(): boolean { return !!this.firstName.trim() && !!this.lastName.trim(); }

  reset(): void {
    const p = this.profile;
    this.firstName = p?.firstName ?? '';
    this.lastName = p?.lastName ?? '';
    this.phoneNumber = p?.phoneNumber ?? '';
  }

  save(): void {
    if (!this.valid) return;
    this.saving = true;
    this.svc.update({ firstName: this.firstName, lastName: this.lastName, phoneNumber: this.phoneNumber })
      .subscribe({
        next: p => {
          this.profile = p; this.reset(); this.saving = false;
          this.snack.open('Profile saved', 'Dismiss', { duration: 4000 });
        },
        error: e => {
          this.saving = false;
          this.snack.open(e?.error?.message ?? 'Could not save your profile', 'Dismiss', { duration: 7000 });
        },
      });
  }
}
